import { useEffect, useState, useCallback } from "react";
import { X, Upload, Trash2, Loader2, Check } from "lucide-react";
import { cms, resolveImg } from "@/lib/cms";
import { toast } from "sonner";

export function MediaGrid({ onSelect, selectable = false, accept = "image/*,video/*" }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await cms.listMedia();
      setMedia(data);
    } catch { toast.error("Failed to load media"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        await cms.uploadMedia(file);
      } catch { toast.error(`Upload failed: ${file.name}`); }
    }
    setUploading(false);
    e.target.value = "";
    toast.success("Uploaded");
    load();
  };

  const del = async (id) => {
    await cms.deleteMedia(id);
    setMedia((m) => m.filter((x) => x.id !== id));
    toast.success("Removed from library");
  };

  return (
    <div>
      <label data-testid="media-upload-label" className="inline-flex items-center gap-2 cursor-pointer border border-gold/50 text-gold px-5 py-3 text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors mb-6">
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
        {uploading ? "Uploading..." : "Upload from device"}
        <input type="file" accept={accept} multiple className="hidden" onChange={handleUpload} data-testid="media-upload-input" />
      </label>

      {loading ? (
        <p className="text-muted2 text-sm">Loading library…</p>
      ) : media.length === 0 ? (
        <p className="text-muted2 text-sm">No media yet. Upload photos from your phone or computer.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {media.map((m) => (
            <div key={m.id} className="group relative aspect-square bg-ink2 overflow-hidden border border-white/10" data-testid={`media-item-${m.id}`}>
              {m.content_type?.startsWith("video") ? (
                <video src={resolveImg(m.url)} className="w-full h-full object-cover" />
              ) : (
                <img src={resolveImg(m.url)} alt={m.original_filename} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {selectable && (
                  <button onClick={() => onSelect(m.url)} data-testid={`media-select-${m.id}`} className="bg-gold text-ink p-2 rounded-full" title="Use this">
                    <Check size={16} />
                  </button>
                )}
                <button onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`} className="bg-red-600 text-white p-2 rounded-full" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MediaPicker({ open, onClose, onSelect }) {
  const [url, setUrl] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-ink/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="media-picker">
      <div className="bg-ink2 border border-white/15 w-full max-w-4xl max-h-[85vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl">Select an image</h3>
          <button onClick={onClose} data-testid="media-picker-close" className="text-muted2 hover:text-cream"><X size={22} /></button>
        </div>
        <div className="flex gap-2 mb-6">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste an image URL" className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm text-cream focus:outline-none focus:border-gold" data-testid="media-picker-url" />
          <button onClick={() => { if (url) { onSelect(url); setUrl(""); } }} className="text-xs uppercase tracking-[0.2em] border border-white/30 px-4 hover:bg-cream hover:text-ink transition-colors">Use URL</button>
        </div>
        <MediaGrid selectable onSelect={onSelect} />
      </div>
    </div>
  );
}
